import React, { useEffect, useRef, useState } from 'react';
import { useMessage } from '../../hooks/useMessage';
import { FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import { Typography } from '@mui/material';
import FormsResultsTable from '../FormsResultsTable/FormsResultsTable';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { transformFormResponses } from '../../utils/forms.utils';

const ViewTableForm = ({ formId, linkedFormId, formAllFields }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const responseIdsToRemove = useRef<(string | number)[]>([]);

    let realFormId = +formId;
    if (linkedFormId) {
        realFormId = linkedFormId;
    }

    useEffect(() => {
        setResultsLoading(true);
        getFormsRows(realFormId)
        .then(response => {
            setResults(response.responses);
        })
        .finally(() => setResultsLoading(false));
    }, [ realFormId ]);

    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);
        Promise.all(
            responseIdsToRemove.current.map((id) => deleteFormResponse(realFormId, id)),
        )
               .then(() => {
                   showMessage('The response(s) has been deleted.', 'success');
                   const filteredResults = results.filter(item => !responseIdsToRemove.current.includes(item.response_id));
                   setResults(filteredResults);
                   responseIdsToRemove.current = [];
               })
               .catch(() => {
                   showMessage('Couldn\'t remove the record(s). Please try again later.', 'error');
               })
               .finally(() => {
                   setConfirmModalOpen(false);
                   setRemoveLoading(false);
               });

    };

    return (
        <div>
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px auto 0', width: '98%' }}>
                    <div>
                        <Typography variant="h4" gutterBottom>Submitted Results</Typography>
                    </div>

                    {formAllFields.length > 0 ? (
                        <FormsResultsTable
                            config={{ fields: formAllFields.filter(field => !field.hidden) }}
                            data={transformFormResponses(results)}
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                </div>
            )}
            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
            <MessageContainer/>
        </div>
    );
};

export default ViewTableForm;